@if (loginHandoffPending) {
  <router-outlet />
} @else {
@if (chromeless()) {
  <!-- Self-contained migrated surface (admin lib / kiosk / studio) — it brings
       its own shell, so ws's chrome is suppressed to avoid nesting. -->
  <router-outlet />
  <!-- A chromeless page (the visitor preview) has no canvas to push, so the
       same panel kinds float over it instead. -->
  @for (kind of pushPanels.all; track kind.kind; let i = $index) {
    <paper-panel-host
      [requests]="kind"
      [layer]="500 + i"
      contentMode="fill"
      [heading]="kind.request()?.target?.heading() ?? null"
      [data-testid]="kind.request()?.target?.testId ?? 'settings.panel.' + kind.kind"
    >
      <ng-template [paperPanelContent]="kind" let-request>
        <ng-template [cdkPortalOutlet]="request.target.portal" />
      </ng-template>
    </paper-panel-host>
  }
} @else {
<!-- ── The workspace nav (One Paper: Home's top nav) ─────────────────────
     Louis's frosted band, pinned over the page. Over a building's plan on the
     indoor map it floats, and the plan runs under it to the window's top. -->
<paper-workspace-nav
  class="ws-nav"
  [placement]="floatingNav() ? 'floating' : 'page'"
  [ariaLabel]="'shell.workspace.nav_label' | transloco"
>
  <ng-container slot="start">
    <!-- Below 640px the side nav is a drawer, opened from here. -->
    <button
      type="button"
      class="ws-nav__menu"
      aria-haspopup="dialog"
      data-testid="open-navigation"
      [attr.aria-expanded]="drawerOpen()"
      [attr.aria-label]="'shell.workspace.open_navigation' | transloco"
      (click)="setDrawerOpen(true)"
    >
      <paper-icon name="panel-left-open" [size]="16" aria-hidden="true" />
    </button>
    <paper-workspace-mark
      [mark]="markInitials()"
      [logo]="markLogo()"
      [organisation]="organisationName()"
      (activated)="goHome()"
    />
    @if (apps().length > 1) {
      <paper-nav-capsule
        class="ws-nav__apps"
        [label]="'shell.workspace.apps_label' | transloco"
        [items]="apps()"
        activeId="settings"
        (selected)="openApp($event.id)"
      />
    }
  </ng-container>

  <paper-search-pill
    slot="centre"
    variant="frost"
    data-testid="open-search"
    [placeholder]="'shell.workspace.search_placeholder' | transloco"
    (clicked)="openSearch()"
  />

  <ng-container slot="end">
    <!-- Paper's end-cluster order: theme, scope, Customers, Ask, profile. -->
    <paper-theme-toggle class="ws-nav__theme" />
    @if (operatorMode()) {
      <!-- Operator console: Ricoh staff working ACROSS customers. The mode
           is marked in the band so nobody mistakes "every customer" for "the
           customer I am in"; the way out is right beside it. -->
      <span class="ws-nav__chip ws-nav__chip--mode">
        <paper-icon name="shield" [size]="14" aria-hidden="true" />
        <span class="ws-nav__chip-label">{{ 'operator.shell.mode' | transloco }}</span>
      </span>
      <a class="ws-nav__chip" routerLink="/overview">
        <paper-icon name="arrow-left" [size]="14" aria-hidden="true" />
        <span class="ws-nav__chip-label">{{ 'operator.shell.exit' | transloco }}</span>
      </a>
    } @else {
      <ws-division-switcher />
      @if (operatorEntry(); as entry) {
        <a
          class="ws-nav__chip ph-no-capture"
          [routerLink]="entry"
          [attr.aria-label]="'operator.shell.entry_aria' | transloco"
        >
          <paper-icon name="building" [size]="14" aria-hidden="true" />
          <span class="ws-nav__chip-label">{{ 'operator.shell.entry' | transloco }}</span>
        </a>
      }
    }
    <!-- The Ask door: opens Ask in the push panel beside the page (⌘J too),
         through Paper's router. On the Overview and /ask, which are Ask, it
         puts the cursor in that page's composer. Without the admin AI
         capability it goes to the Overview, which says how to turn Ask on,
         rather than being a button that does nothing. -->
    <paper-ask-chip
      class="ws-nav__ask"
      data-testid="ask-admin"
      [shortcut]="askAvailable() ? '⌘J' : null"
      [label]="'shell.workspace.ask_admin' | transloco"
      [open]="askHosts.isOpen()"
      [markState]="askState()"
      (activated)="askAdmin($event)"
    />
    <paper-profile-panel
      data-testid="profile-panel"
      [identity]="profileIdentity()"
      [loading]="profileLoading()"
      [actions]="profileActions()"
      [open]="profileOpen()"
      (openChange)="setProfileOpen($event)"
      (action)="onProfileAction($event)"
    >
      <ws-workspace-switcher slot="extra" [panelOpen]="profileOpen()" />
    </paper-profile-panel>
  </ng-container>
</paper-workspace-nav>

<div
  class="ws-workspace"
  [class.ws-workspace--floating]="floatingNav()"
>
  <!-- Louis's side nav: ADMIN_NAV, gated, with its filter; folds to icons and
       becomes a drawer below 640px. The operator console keeps its own. -->
  @if (operatorMode()) {
    <paper-workspace-rail
      class="ws-rail"
      data-testid="settings.operator.nav"
      mode="sidenav"
      stateKey="operator"
      [label]="'operator.shell.mode' | transloco"
      [sections]="operatorSections()"
      [activeKey]="activeKey() ?? null"
      [drawerOpen]="drawerOpen()"
      (drawerOpenChange)="setDrawerOpen($event)"
    />
  } @else {
    <ws-admin-rail-nav
      class="ws-rail"
      [label]="'shell.workspace.app_settings' | transloco"
      [groups]="navGroups()"
      [activeKey]="activeKey()"
      [drawerOpen]="drawerOpen()"
      (drawerOpenChange)="setDrawerOpen($event)"
    />
  }

  <!-- The canvas and its push panels: a page's side panel opens as a column
       beside the canvas and pushes it over (Manage's layout, COMMON ruling 5).
       One host per panel kind (WsPushPanels); the content is the page's own,
       handed over as a portal by ws-push-panel. -->
  <paper-panel-layout class="ws-layout">
    <main paperPanelCanvas class="canvas" [class.canvas--full-bleed]="fullBleedCanvas()">
      <!-- A detail page's record chain: parent list › record › n of N. A list
           page names itself in its own header, so nothing is drawn there. -->
      @if (chrome.record()) {
        <ws-record-chain class="record-chain" />
      }
      <!-- Switch landing: the switch succeeded into a customer with no employee
           row for this person. Consequence-led, dismissible, one line — the
           person can read everything and save nothing until a platform
           operator seeds a role. -->
      @if (switchLanding.notice(); as landing) {
        <paper-banner
          class="switch-landing"
          tone="warning"
          icon="alert-triangle"
          [dismissible]="true"
          [dismissLabel]="'shell.switch_landing.dismiss' | transloco"
          (dismissed)="switchLanding.dismiss()"
        >
          {{ 'shell.switch_landing.no_role' | transloco: {
              customer: landing.customerName || ('shell.switch_landing.this_customer' | transloco)
          } }}
        </paper-banner>
      }
      <!-- A Ricoh support session: say whose customer this is, until when,
           and offer the way back. Information only — the API refuses an
           ended session on every request, whatever this shows. -->
      @if (supportSession.session(); as session) {
        <paper-banner
          class="support-session ph-no-capture"
          [tone]="supportSession.ended() ? 'warning' : 'info'"
          icon="shield"
        >
          <span class="support-session__text">
            @if (supportSession.ended()) {
              {{ 'operator.session_banner.ended' | transloco: { customer: session.customerName || ('operator.session_banner.this_customer' | transloco) } }}
            } @else {
              {{ 'operator.session_banner.active' | transloco: {
                  customer: session.customerName || ('operator.session_banner.this_customer' | transloco),
                  time: (session.expiresAt | date: 'HH:mm')
              } }}
            }
          </span>
          <paper-button
            class="support-session__back"
            intent="secondary"
            size="sm"
            [loading]="returningHome()"
            (click)="backToCustomers()"
          >
            {{ 'operator.session_banner.back' | transloco }}
          </paper-button>
        </paper-banner>
      }
      <!-- Cold load: the guards are still checking the session and no page
           exists yet. -->
      @if (!firstNavigationDone()) {
        <div class="canvas-loading" role="status" [attr.aria-label]="'common.loading' | transloco">
          <paper-skeleton variant="line" height="28px" width="30%" />
          <paper-skeleton variant="line" height="16px" width="55%" />
          <paper-skeleton height="180px" />
          <paper-skeleton height="240px" />
        </div>
      }
      <router-outlet />
    </main>
    @for (kind of pushPanels.all; track kind.kind; let i = $index) {
      <!-- Test ids (ruling 17): the page names its panel (ws-push-drawer's or
           ws-push-panel's testId), else the kind does. Paper derives .title,
           .close, .expand, .surface and .resize from it. -->
      <paper-panel-host
        layout="push"
        contentMode="fill"
        [requests]="kind"
        [order]="i + 1"
        [heading]="kind.request()?.target?.heading() ?? null"
        [data-testid]="kind.request()?.target?.testId ?? 'settings.panel.' + kind.kind"
      >
        <ng-template [paperPanelContent]="kind" let-request>
          <ng-template [cdkPortalOutlet]="request.target.portal" />
        </ng-template>
      </paper-panel-host>
    }
    <!-- Ask in a push panel, beside any page: the host the "Ask Admin" chip,
         ⌘J and the palette open Ask in. The surface draws its own header
         (Open full page, close), so the host draws none. A link in an answer
         opens its page in the canvas beside it.
         New chat is ours, not the surface's icon-only pencil: a labelled
         pill in the header, there whenever a thread is (Nath, 8 Oct: "you
         can't ever start a new chat"). -->
    @if (askAvailable()) {
      <paper-panel-host
        class="ws-ask-panel"
        data-testid="settings.ask.panel"
        layout="push"
        [order]="100"
        [requests]="askPanel"
        [heading]="null"
        [label]="'settings_ask.title' | transloco"
      >
        <ng-template [paperPanelContent]="askPanel">
          <paper-ask-surface
            #askSurface
            class="ws-ask-in-panel"
            mode="panel"
            surface="panel"
            [announceProgress]="true"
            [expandable]="true"
            expandKind="page"
            [showNewChat]="false"
            [name]="askName()"
            [prompt]="'settings_ask.prompt' | transloco"
            [placeholder]="'settings_ask.placeholder' | transloco"
            (expandToggled)="askRouter.openFullPage()"
            (closed)="askPanel.close()"
            (suggestionOpened)="onAskStep($event)"
          >
            @if (askHasThread()) {
              <paper-button
                askActions
                class="ws-ask-new-chat"
                intent="tertiary"
                size="xs"
                icon="plus"
                data-testid="settings.ask.panel.header.new-chat"
                [paperTooltip]="'settings_ask.new_chat_hint' | transloco"
                (click)="askNewChat(askSurface)"
              >
                {{ 'settings_ask.new_chat' | transloco }}
              </paper-button>
            }
          </paper-ask-surface>
        </ng-template>
      </paper-panel-host>
    }
  </paper-panel-layout>
</div>

<!-- The intro (features/intro): on an admin's first visit, and from /welcome,
     the profile menu and ⌘K. Its own chunk, loaded when it opens. -->
@if (intro.open()) {
  @defer (on immediate) {
    <ws-settings-intro />
  }
}

<!-- ⌘K: the section jumper and Ask, over Paper's action registry. Mounted
     with the chrome only: chromeless surfaces bring their own. -->
<paper-command-palette [placeholder]="'shell.workspace.palette_placeholder' | transloco" />
}

}

<!-- The floating drawer (ws-push-drawer [floating]): over the canvas, not a
     column beside it, for a quick create that would otherwise squeeze its
     page too narrow to read. No layout="push", so it floats on every route. -->
<paper-panel-host
  [requests]="pushPanels.floatingDrawer"
  [layer]="600"
  contentMode="fill"
  [heading]="pushPanels.floatingDrawer.request()?.target?.heading() ?? null"
  [data-testid]="pushPanels.floatingDrawer.request()?.target?.testId ?? 'settings.panel.ws-floating-drawer'"
>
  <ng-template [paperPanelContent]="pushPanels.floatingDrawer" let-request>
    <ng-template [cdkPortalOutlet]="request.target.portal" />
  </ng-template>
</paper-panel-host>

<!-- Toasts, for every route, chromeless included. PaperToastService only queues
     them: with no stack mounted here, every toast.show() in the app was silent. -->
<paper-toast-stack />
